import { useEffect, useState } from "react";
import { Lock, Loader2, Sparkles, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { createCheckout, getCreatorId, getSubscription } from "../lib/api";
import { useUnlocks } from "../lib/unlocks";
import { PLANS, PERSONA_ONLY_INCLUDES, PRO_PASS_EXTRAS } from "../lib/plans";

// PaywallGate — routes users to the RIGHT tier for the feature they hit.
// tier="persona_only" upsells the $2 tier first, with a link out to Pro Pass.
// tier="pro_pass" upsells only the full bundle.
export default function PaywallGate({ feature, tier = "pro_pass", children }) {
  const { unlocks, loading, has } = useUnlocks();
  const [buying, setBuying] = useState(null);
  const [trialEligible, setTrialEligible] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(
    tier === "persona_only" ? "persona_only_monthly" : "pro_pass_monthly"
  );

  useEffect(() => {
    getSubscription(getCreatorId())
      .then((s) => setTrialEligible(!!s?.trial_eligible))
      .catch(() => {});
  }, []);

  const buy = async (lookup_key) => {
    setBuying(lookup_key);
    try {
      const { checkout_url } = await createCheckout(lookup_key, getCreatorId());
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setBuying(null);
    }
  };

  if (loading && unlocks === null) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF3B30]" />
      </div>
    );
  }

  // pro_pass grants everything; persona_only satisfies persona_only routes.
  if (tier === "pro_pass" && has("pro_pass")) return children;
  if (tier === "persona_only" && (has("persona_only") || has("pro_pass"))) return children;

  return tier === "persona_only" ? (
    <PersonaOnlyPaywall
      feature={feature}
      selectedPlan={selectedPlan}
      setSelectedPlan={setSelectedPlan}
      buying={buying}
      onBuy={buy}
      trialEligible={trialEligible}
    />
  ) : (
    <ProPassPaywall
      feature={feature}
      selectedPlan={selectedPlan}
      setSelectedPlan={setSelectedPlan}
      buying={buying}
      onBuy={buy}
      trialEligible={trialEligible}
    />
  );
}

// ---------------------------------------------------------------------------
// Persona-Only paywall — highlights the $2 tier but lets users step up to Pro Pass.
// ---------------------------------------------------------------------------
function PersonaOnlyPaywall({ feature, selectedPlan, setSelectedPlan, buying, onBuy, trialEligible }) {
  const planKeys = ["persona_only_monthly", "pro_pass_monthly", "pro_pass_yearly"];
  return (
    <div className="mx-auto max-w-[1100px] px-6 py-14 md:px-10" data-testid="paywall-gate-persona-only">
      <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// {feature || "Locked"}</p>
      <h1 className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-tight text-white md:text-8xl">
        Go deeper<br />on your taste
      </h1>
      <p className="mt-4 max-w-2xl text-sm text-neutral-400">
        {feature ? `${feature} is part of the Persona Only plan (or Pro Pass).` : "Unlock the deeper Persona toolset."} Just <span className="text-white">$2/month</span> — cancel anytime.
      </p>

      <div className="mt-10 grid gap-6 md:grid-cols-[1fr_auto]">
        <div className="border border-white/15 bg-neutral-950 p-8">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#FF3B30]" />
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">Persona Only includes</p>
          </div>
          <ul className="mt-4 space-y-2">
            {PERSONA_ONLY_INCLUDES.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-neutral-200">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#FF3B30]" />
                {f}
              </li>
            ))}
          </ul>
          <div className="mt-6 border-t border-white/10 pt-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">Want more? Pro Pass adds</p>
            <ul className="mt-2 space-y-1.5 text-xs text-neutral-500">
              {PRO_PASS_EXTRAS.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <span className="text-[#FF3B30]">+</span> {f}
                </li>
              ))}
            </ul>
            <Link
              to="/premium"
              data-testid="paywall-see-pro-pass"
              className="mt-4 inline-flex items-center gap-1 border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white hover:border-[#FF3B30]"
            >
              See all Pro Pass benefits →
            </Link>
          </div>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            Your catalogue, ratings, and notes always stay free.
          </p>
        </div>

        <PlanPicker
          planKeys={planKeys}
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          buying={buying}
          onBuy={onBuy}
          trialEligible={trialEligible}
          primaryLabel="Unlock Persona"
        />
      </div>

      <p className="mt-6 font-mono text-[10px] uppercase tracking-widest text-neutral-600">
        Payments handled by Stripe · Cancel or manage anytime from your account
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pro Pass paywall — Discover / Schedule / Import lock straight to full bundle.
// ---------------------------------------------------------------------------
function ProPassPaywall({ feature, selectedPlan, setSelectedPlan, buying, onBuy, trialEligible }) {
  const planKeys = ["pro_pass_monthly", "pro_pass_yearly"];
  return (
    <div className="mx-auto max-w-[1100px] px-6 py-14 md:px-10" data-testid="paywall-gate-pro-pass">
      <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// {feature || "Locked"}</p>
      <h1 className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-tight text-white md:text-8xl">
        Pro Pass<br />Required
      </h1>
      <p className="mt-4 max-w-2xl text-sm text-neutral-400">
        {feature ? `${feature} is a Pro Pass feature.` : "This section is part of the Pro Pass."} <span className="text-white">$5/month</span> — everything Persona Only unlocks, plus the full app.
        {trialEligible && " Start with 7 days free."}
      </p>

      <div className="mt-10 grid gap-6 md:grid-cols-[1fr_auto]">
        <div className="border border-white/15 bg-neutral-950 p-8">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#FF3B30]" />
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">Pro Pass includes</p>
          </div>
          <ul className="mt-4 space-y-2">
            {[...PERSONA_ONLY_INCLUDES, ...PRO_PASS_EXTRAS].map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-neutral-200">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#FF3B30]" />
                {f}
              </li>
            ))}
          </ul>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            Your catalogue, ratings, and notes always stay free.
          </p>
        </div>

        <PlanPicker
          planKeys={planKeys}
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          buying={buying}
          onBuy={onBuy}
          trialEligible={trialEligible}
          primaryLabel={trialEligible ? "Start free trial" : "Start Pro Pass"}
        />
      </div>

      <p className="mt-6 font-mono text-[10px] uppercase tracking-widest text-neutral-600">
        Payments handled by Stripe · Cancel or manage anytime from your account
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared plan-picker column
// ---------------------------------------------------------------------------
function PlanPicker({ planKeys, selectedPlan, setSelectedPlan, buying, onBuy, trialEligible, primaryLabel }) {
  return (
    <div className="min-w-[280px] border border-white/15 bg-neutral-950 p-6">
      <div className="grid grid-cols-1 gap-2" data-testid="paywall-plan-picker">
        {planKeys.map((key) => {
          const p = PLANS[key];
          if (!p) return null;
          const isSelected = selectedPlan === key;
          return (
            <button
              key={key}
              data-testid={`paywall-plan-${key}`}
              onClick={() => setSelectedPlan(key)}
              className={`relative border px-3 py-3 text-left font-mono text-[10px] uppercase tracking-widest transition-colors ${isSelected ? "border-[#FF3B30] bg-[#FF3B30]/10" : "border-white/15 hover:border-white/40"}`}
            >
              {p.badge && (
                <span className="absolute -top-2 right-2 border border-[#FF3B30] bg-[#FF3B30] px-1.5 py-0.5 text-[8px] tracking-widest text-white">
                  {p.badge}
                </span>
              )}
              <p className={isSelected ? "text-[#FF3B30]" : "text-neutral-400"}>{p.label}</p>
              <p className="mt-1 flex items-baseline gap-1">
                <span className="font-display text-3xl text-white">{p.price}</span>
                <span className="text-[9px] tracking-widest text-neutral-500">{p.per}</span>
              </p>
              <p className="mt-1 text-[9px] tracking-widest text-neutral-500">{p.tagline}</p>
            </button>
          );
        })}
      </div>

      {trialEligible && selectedPlan.startsWith("pro_pass") && (
        <p data-testid="paywall-trial-note" className="mt-4 flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-emerald-400">
          <Sparkles className="h-3 w-3" /> 7 days free · Cancel before it charges
        </p>
      )}

      <button
        data-testid="unlock-btn"
        onClick={() => onBuy(selectedPlan)}
        disabled={buying != null}
        className="mt-4 flex w-full items-center justify-center gap-2 border border-white/20 bg-[#FF3B30] px-6 py-4 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black disabled:opacity-50"
      >
        {buying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
        {primaryLabel}
      </button>
    </div>
  );
}
